import {
  Divider,
  ErrorMessages, IconButton, IconButtonType, Page, Spinner
} from "@whitespace/whitespace-ui";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import ICollector from "interfaces/ICollector";
import IDisposalSite from "interfaces/IDisposalSite";
import IRaiseWorkOrderRequestModel from "interfaces/Request/IRaiseWorkOrderRequestModel";
import { useStateMachine } from "little-state-machine";
import { useCallback, useEffect, useState } from "react";
import {
  Breadcrumb,
  Button,
  Card, Col, Form, Row
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { DateTimePickerComponent } from "@syncfusion/ej2-react-calendars";
import INonWorkingPeriod from "interfaces/INonWorkingPeriod";
import AmplifyError from "components/AmplifyError/AmplifyError";
import ISlaDetails from "interfaces/ISlaDetails";
import { formatDecimalToHoursAndMinutes } from "helpers/DateUtils";
import { makeDateTimePickerAACompliant } from "modules/SyncfusionAAFixer";

import "./RaiseWorkOrder.scss";
import AlertDismissable from "components/AlertDismissable/AlertDIsmissable";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import { format } from "date-fns";

const RaiseWorkOrder = () => {
  const navigate = useNavigate();
  const { state } = useStateMachine();
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    setValue,
    watch
  } = useForm<IRaiseWorkOrderRequestModel>({
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const [slaStart, setSlaStart] = useState<Date>(new Date(Date.now()));
  const [slaStartMin, setSlaStartMin] = useState<Date>();
  const [nonWorkingPeriods, setNonWorkingPeriods] = useState<INonWorkingPeriod[]>();
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [raiseWorkOrderButtonDisabled, setRaiseWorkOrderButtonDisabled] = useState<boolean>(true);

  const {
    isSending: getCollectorsIsSending, errors: getCollectorsErrors, data: getCollectorsData, sendRequest: getCollectorsSendRequest
  } = useAmplifyREST();

  const {
    isSending: getSlaDetailsIsSending, errors: getSlaDetailsErrors, data: getSlaDetailsData, sendRequest: getSlaDetailsSendRequest
  } = useAmplifyREST();

  const {
    isSending: getDisposalSitesIsSending, errors: getDisposalSitesErrors, data: getDisposalSitesData, sendRequest: getDisposalSitesSendRequest
  } = useAmplifyREST();

  const {
    isSending: getNonWorkingPeriodsIsSending, errors: getNonWorkingPeriodsErrors, data: getNonWorkingPeriodsData, sendRequest: getNonWorkingPeriodsSendRequest
  } = useAmplifyREST();

  const {
    isSending: raiseWorkOrderIsSending, errors: raiseWorkOrderErrors, statusCode: raiseWorkOrderStatusCode, sendRequest: raiseWorkOrderSendRequest
  } = useAmplifyREST();

  const isSending = getCollectorsIsSending || getSlaDetailsIsSending || getDisposalSitesIsSending || getNonWorkingPeriodsIsSending || raiseWorkOrderIsSending;
  const apiErrors = getCollectorsErrors || getSlaDetailsErrors || getDisposalSitesErrors || getNonWorkingPeriodsErrors || raiseWorkOrderErrors;
  const initialApiLoadComplete = getCollectorsData && getSlaDetailsData && getDisposalSitesData;

  if (state.raiseWorkOrderDetails) {
    useEffect(() => {
      getCollectorsSendRequest(`regions/${state.raiseWorkOrderDetails!.regionId}/collectors/active`, "GET");
    }, [getCollectorsSendRequest]);

    useEffect(() => {
      getSlaDetailsSendRequest("settings/sla/details", "GET");
    }, [getSlaDetailsSendRequest]);

    useEffect(() => {
      getDisposalSitesSendRequest(`regions/${state.raiseWorkOrderDetails!.regionId}/disposalsites/active`, "GET");
    }, [getDisposalSitesSendRequest]);
  }

  const renderCollectors = () => {
    const collectors = [
      <option key="" value="">Please Select</option>
    ];
    if (getCollectorsData) {
      (getCollectorsData.collectors as ICollector[]).forEach(collector => {
        collectors.push(<option key={collector.id} value={collector.id}>{`${collector.name} - ${collector.type}`}</option>);
      });
    }

    return collectors;
  };

  const renderSlaTimes = () => {
    const slaTimes :any = [];

    if (getSlaDetailsData) {
      (getSlaDetailsData.slaDetails as ISlaDetails).slaTimes.forEach(slaTime => {
        slaTimes.push(<option key={`slaTime_${slaTime}`} value={slaTime}>{formatDecimalToHoursAndMinutes(slaTime)}</option>);
      });
    }

    return slaTimes;
  };

  const renderDisposalSites = () => {
    const disposalSites = [
      <option key="" value="">Please Select</option>
    ];
    if (getDisposalSitesData) {
      (getDisposalSitesData.disposalSites as IDisposalSite[]).forEach(disposalSite => {
        disposalSites.push(<option key={disposalSite.id} value={disposalSite.id}>{disposalSite.name}</option>);
      });
    }

    return disposalSites;
  };

  const renderNonWorkingPeriods = () => {
    if (getValues("disposalSiteId").length !== 0 && nonWorkingPeriods?.length === 0) {
      return <p>No Non-Working Periods Found for this Disposal Site</p>;
    }
    return nonWorkingPeriods?.map(nonWorkingPeriod => {
      let variant = nonWorkingPeriod.severity.toLocaleLowerCase();
      switch (nonWorkingPeriod.severity.toLocaleLowerCase()) {
        case "alert": {
          variant = "danger";
          break;
        }
        case "information": {
          variant = "info";
          break;
        }
        default:
          break;
      }
      return (
        <AlertDismissable
          key={`${nonWorkingPeriod.id}_${format(new Date(), "HH:mm:ss")}`}
          id={nonWorkingPeriod.id}
          label={nonWorkingPeriod.description}
          variant={variant}
        />
      );
    });
  };

  const watchFields = watch([
    "collectorId",
    "slaTime",
    "disposalSiteId"
  ]);

  const updateNonWorkingPeriods = (disposalSiteId : string) => {
    if (disposalSiteId) getNonWorkingPeriodsSendRequest(`disposalsites/${disposalSiteId}/nonworkingperiods/active`, "GET");
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const canFormBeSubmitted = () => {
    if (!getValues("collectorId")) {
      return;
    }

    const collectorId = getValues("collectorId");
    const disposalSiteRequired = (getCollectorsData.collectors as ICollector[]).find(collector => collector.id === collectorId)?.isVehicle === true;

    setRaiseWorkOrderButtonDisabled(
      !(
        collectorId.length > 0
        && (!disposalSiteRequired || getValues("disposalSiteId").length > 0)
        && slaStart
        && getValues("slaTime") !== 0
      )
    );
  };

  useEffect(() => {
    canFormBeSubmitted();
  }, [watchFields]);

  useEffect(() => {
    canFormBeSubmitted();
  }, [slaStart]);

  useEffect(() => {
    const slaTimeMin = new Date(Date.now());
    slaTimeMin.setMinutes(slaTimeMin.getMinutes() <= 29 ? 0 : 30);
    setSlaStartMin(slaTimeMin);
  }, []);

  useEffect(() => {
    if (getNonWorkingPeriodsData) {
      setNonWorkingPeriods(getNonWorkingPeriodsData.nonWorkingPeriods);
    }
  }, [getNonWorkingPeriodsData]);

  const handleRaiseWorkOrderClick = (request : IRaiseWorkOrderRequestModel) => {
    setShowExceptions(true);
    request.slaStart = slaStart.toISOString();
    raiseWorkOrderSendRequest(`container/${encodeURI(state.raiseWorkOrderDetails?.containerId!)}/workorder`, "POST", request);
  };

  useEffect(() => {
    if (raiseWorkOrderStatusCode === 200) {
      navigate(state.raiseWorkOrderDetails?.pageOrigination!);
    }
  }, [raiseWorkOrderStatusCode]);

  useEffect(() => {
    if (initialApiLoadComplete) {
      makeDateTimePickerAACompliant();
    }
  }, [initialApiLoadComplete]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <h2>
            Raise Work Order
            <Spinner
              animation="grow"
              visible={isSending}
              spinnerText="Loading"
              variant="info"
            />

          </h2>
        </Card.Header>
        <Form autoComplete="off" onSubmit={handleSubmit(handleRaiseWorkOrderClick)}>
          <Card.Body className="no-padding">
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={12} lg={8} className="d-flex">
                {state.raiseWorkOrderDetails
                && (
                  <Breadcrumb className="left-margin-large no-wrap">
                    <Breadcrumb.Item
                      data-testid={state.raiseWorkOrderDetails!.pageOrigination === RoutePathsEnum.Containers
                        ? "breadcrumb-container-status"
                        : "breadcrumb-work-orders"}
                      linkAs={Link}
                      linkProps={{ to: state.raiseWorkOrderDetails!.pageOrigination }}
                      onClick={() => navigate(state.raiseWorkOrderDetails!.pageOrigination)}
                    >
                      {state.raiseWorkOrderDetails.pageOrigination === RoutePathsEnum.Containers ? "Container Status" : "Work Orders"}
                    </Breadcrumb.Item>

                    <Breadcrumb.Item data-testid="breadcrumb-raise-work-order" active>
                      Raise Work Order
                    </Breadcrumb.Item>
                  </Breadcrumb>
                ) }
              </Col>
              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={raiseWorkOrderButtonDisabled || isSending}
                  aria-disabled={raiseWorkOrderButtonDisabled || isSending}
                  aria-label="Raise work order"
                  type="submit"
                  spin={raiseWorkOrderIsSending}
                  data-testid="buttonRaiseWorkOrder"
                >
                  Raise
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(state.raiseWorkOrderDetails?.pageOrigination!)}
                  data-testid="buttonCancel"
                  aria-label={`Cancel raise work order and back to ${state.raiseWorkOrderDetails?.pageOrigination === RoutePathsEnum.Containers
                    ? "containers"
                    : "work orders"}`}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            {initialApiLoadComplete
            && (
              <section className="raise-work-order pt-2">
                <Row className="mt-1 ms-1">
                  <Col xs={12} md={8}>
                    <div className="control-pane">
                      <div className="control-section">

                        <div className="d-table w-100 horiz-form-label form-label">
                          <div className="d-table-row" data-testid="hwrcSiteName">
                            <div className="d-table-cell pb-2">Site:</div>
                            <div className="d-table-cell pb-2">{state.raiseWorkOrderDetails!.hwrcSiteName}</div>
                          </div>
                          <div className="d-table-row" data-testid="regionName">
                            <div className="d-table-cell pb-2">Region:</div>
                            <div className="d-table-cell pb-2">{state.raiseWorkOrderDetails!.regionName}</div>
                          </div>
                          <div className="d-table-row" data-testid="containerType">
                            <div className="d-table-cell pb-2">Type :</div>
                            <div className="d-table-cell pb-2">{state.raiseWorkOrderDetails!.containerType}</div>
                          </div>
                        </div>
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="collectorId">
                            <Form.Label id="collectorLabel" className="horiz-form-label">
                              Allocate To:
                            </Form.Label>
                            <Col>
                              <Form.Select
                                {...register("collectorId", { onChange: () => canFormBeSubmitted() })}
                                data-testid="ddlCollectors"
                                aria-required
                                aria-invalid={false}
                                aria-labelledby="collectorLabel"
                              >
                                {renderCollectors()}
                              </Form.Select>
                            </Col>
                            <ErrorMessages fieldName="collectorId" errors={errors} validationFailures={null} />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="slaStart">
                            <Form.Label id="slaStartLabel" className="horiz-form-label">
                              SLA Start:
                            </Form.Label>
                            <Col>
                              <DateTimePickerComponent
                                data-testid="slaStart"
                                id="slaStart"
                                min={slaStartMin}
                                format="dd/MM/yyyy HH:mm"
                                timeFormat="HH:mm"
                                value={slaStart}
                                change={(dtp:any) => { setSlaStart(dtp.value); }}
                                htmlAttributes={{ "aria-labelledby": "slaStartLabel" }}
                              />

                            </Col>
                            <ErrorMessages fieldName="slaStart" errors={errors} validationFailures={null} />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="slaTime">
                            <Form.Label id="slaTimeLabel" className="horiz-form-label">
                              Set SLA:
                            </Form.Label>
                            <Col>
                              <Form.Select
                                {...register("slaTime", { onChange: () => canFormBeSubmitted() })}
                                data-testid="ddlSlaTimes"
                                aria-required
                                aria-invalid={false}
                                aria-labelledby="slaTimeLabel"
                                defaultValue={(getSlaDetailsData.slaDetails as ISlaDetails).slaDefaultTime}
                              >
                                {renderSlaTimes()}

                              </Form.Select>

                            </Col>
                            <ErrorMessages fieldName="slaStart" errors={errors} validationFailures={null} />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="disposalSiteId">
                            <Form.Label id="disposalSiteLabel" className="horiz-form-label">
                              Disposal Site:
                            </Form.Label>
                            <Col>
                              <Form.Select
                                {...register("disposalSiteId", { onChange: () => canFormBeSubmitted() })}
                                data-testid="ddlDisposalSites"
                                aria-required
                                aria-invalid={false}
                                aria-labelledby="disposalSiteLabel"
                                onChange={(e : any) => { setValue("disposalSiteId", e.target.value!); updateNonWorkingPeriods(e.target.value); }}
                              >
                                {renderDisposalSites()}
                              </Form.Select>
                            </Col>
                            <ErrorMessages fieldName="disposalSiteId" errors={errors} validationFailures={null} />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="comments">
                            <Form.Label
                              id="commentsLabel"
                              className="horiz-form-label"
                            >
                              Comments:
                            </Form.Label>
                            <Col>
                              <Form.Control
                                type="text"
                                role="textbox"
                                aria-required="false"
                                aria-invalid={false}
                                aria-labelledby="commentsLabel"
                                maxLength={50}
                                {...register("comments")}
                              />
                            </Col>
                            <ErrorMessages
                              fieldName="comments"
                              errors={errors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} lg={4}>
                    <div className="d-flex h-100">
                      <div className="non-working-period-container">
                        {nonWorkingPeriods && renderNonWorkingPeriods()}
                      </div>
                    </div>
                  </Col>
                </Row>
              </section>
            ) }
          </Card.Body>
        </Form>
      </Card>
    </Page>
  );
};

export default RaiseWorkOrder;
